import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import gloriousLogo from "@/assets/glorious-logo.webp";
import { startProjectStore } from "@/lib/startProjectStore";

interface NavbarProps {
  visible: boolean;
}

const NAV_ITEMS: { label: string; type: "section" | "route"; target: string }[] = [
  { label: "Services", type: "section", target: "services" },
  { label: "Case Studies", type: "section", target: "case-studies" },
  { label: "Who We Are", type: "route", target: "/company" },
];

const Navbar = ({ visible }: NavbarProps) => {
  const [lightMode, setLightMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      // Only landing page has dark sections; on other routes, always dark text on light bg.
      if (location.pathname !== "/") {
        setLightMode(false);
        return;
      }
      const navY = 60;
      const heroEl = document.getElementById("hero-section");
      const footerEl = document.getElementById("footer-section");

      let isOnDark = false;

      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        if (navY >= rect.top && navY <= rect.bottom) isOnDark = true;
      }
      if (footerEl) {
        const rect = footerEl.getBoundingClientRect();
        if (navY >= rect.top && navY <= rect.bottom) isOnDark = true;
      }

      setLightMode(isOnDark);
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const goToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      className={cn(
        "fixed top-4 md:top-6 left-1/2 -translate-x-1/2 z-50 glass-nav rounded-2xl px-4 md:px-6 py-2.5 md:py-3 flex items-center justify-between transition-all duration-1000 ease-out",
        "w-[94vw] md:w-[90vw] max-w-5xl",
        visible
          ? "translate-y-0 opacity-100"
          : "-translate-y-24 opacity-0"
      )}
    >
      <div className="flex items-center gap-8">
        <Link to="/" aria-label="Glorious — home">
          <img
            src={gloriousLogo}
            alt="Glorious agency logo"
            className={cn(
              "h-8 md:h-10 w-auto transition-all duration-500",
              lightMode ? "brightness-[10]" : "brightness-0"
            )}
          />
        </Link>
        <div className="hidden md:flex items-center gap-6">
          {NAV_ITEMS.map((item) => {
            const className = cn(
              "relative text-sm font-medium transition-all duration-500 font-nunito cursor-pointer bg-transparent border-0 p-0",
              lightMode ? "text-white/80 hover:text-white" : "text-foreground/70 hover:text-foreground"
            );
            const onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.textShadow =
                "0 0 12px hsla(205,90%,62%,0.8), 0 0 24px hsla(205,90%,62%,0.5), 0 0 40px hsla(205,90%,62%,0.3)";
            };
            const onMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.textShadow = "none";
            };
            if (item.type === "route") {
              return (
                <Link
                  key={item.label}
                  to={item.target}
                  className={className}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => goToSection(item.target)}
                className={className}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => startProjectStore.open()}
        className="group glass-button rounded-full px-4 md:px-6 py-2 md:py-2.5 font-cursive text-sm md:text-base font-semibold text-primary-foreground hover:scale-105 transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap"
      >
        Work With Us
        <ArrowUpRight
          size={16}
          className="md:w-[18px] md:h-[18px] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
        />
      </button>
    </nav>
  );
};

export default Navbar;
